/**
 * The Venues roster endpoint, typed (BON-538).
 *
 * Mirrors `backend/src/services/venue-roster.service.ts`. One call serves the
 * whole page: the roster, the map's pins and the scope block behind the region
 * picker, so the roster count and the pin count cannot disagree — they are the
 * same array.
 *
 * Region scoping is a slug at any depth and rolls up everything beneath it,
 * which is why there is one `region` parameter rather than a region and a metro.
 */
import { useQuery } from '@tanstack/react-query';

import { RegionNotFoundError } from '../../../../api/region-not-found-error';
import { apiFetch } from '../../../../lib/apiFetch';
import { regionSlugFromParam } from '../../primitives/scope/region-param';

const STALE_30S = 30_000;

/** The query-key prefix of every roster request. Note writes invalidate by it. */
export const ROSTER_KEY = ['venues.roster'] as const;

export type VenueRosterSort = 'name' | 'totalRun' | 'upcoming' | 'sold' | 'openActionItemCount';

/** A region as the roster and its picker need it. */
export interface RosterRegionRef {
  id: string;
  slug: string;
  name: string;
  color: string | null;
}

export interface VenueRosterRow {
  id: string;
  slug: string | null;
  name: string;
  city: string;
  addressLine1: string;
  state: string;
  /** The venue's own zone, raw — fall back to its state's zone when null. */
  timeZone: string | null;
  latitude: number | null;
  longitude: number | null;
  region: (RosterRegionRef & { parent: RosterRegionRef | null }) | null;
  /** Active workshops that have already happened. */
  totalRun: number;
  /** Active workshops still to come — all of them, not a fixed window. */
  upcoming: number;
  /** Seats across those upcoming workshops. */
  capacity: number;
  /** Confirmed seats sold against those same workshops. */
  sold: number;
  /** ISO date. The venue row's creation date — see the endpoint's caveat. */
  partnerSince: string;
  /** Open action items on this venue. 0 when there are none. */
  openActionItemCount: number;
}

export interface VenueRosterScope {
  region: RosterRegionRef;
  /** Top down, excluding the region itself. */
  ancestors: RosterRegionRef[];
  children: RosterRegionRef[];
}

export interface VenueRosterResult {
  venues: VenueRosterRow[];
  /** Null for every region, and also when two or more regions were asked for. */
  scope: VenueRosterScope | null;
  /** Venues matching the filters, ignoring `limit`/`offset` — so a caller can
   *  tell "that is all of them" from "that is the first page". */
  total: number;
}

export { RegionNotFoundError };

/** What the roster is asked for. */
export interface VenueRosterQuery {
  /** Region slugs; the venues of all of them. Empty or absent is every region. */
  regions?: string[];
  /** Only venues that have open action items. */
  hasOpenTasks?: boolean;
}

/** The slugs actually worth sending: "all" is a choice in the address, not a region. */
const realSlugs = (regions: string[] | undefined): string[] =>
  (regions ?? []).filter((slug) => !!regionSlugFromParam(slug));

export async function fetchVenueRoster(request: VenueRosterQuery = {}): Promise<VenueRosterResult> {
  const slugs = realSlugs(request.regions);
  const query = new URLSearchParams({ isActive: 'true' });
  // Several regions travel as one comma-separated parameter.
  if (slugs.length > 0) query.set('region', slugs.join(','));
  if (request.hasOpenTasks) query.set('hasOpenTasks', 'true');

  const res = await apiFetch(`/api/v1/venues?${query}`);
  if (res.status === 404 && slugs.length > 0) throw new RegionNotFoundError(slugs.join(', '));
  if (!res.ok) throw new Error(`Venue roster request failed (${res.status})`);
  return (await res.json()) as VenueRosterResult;
}

const PAGE_SIZE = 500;

/** Every active venue, paging past the server's 500-row cap so a long roster is
 *  never silently cut short. */
export async function fetchAllActiveVenueRoster(): Promise<VenueRosterRow[]> {
  const rows: VenueRosterRow[] = [];
  for (;;) {
    const query = new URLSearchParams({
      isActive: 'true',
      limit: String(PAGE_SIZE),
      offset: String(rows.length),
    });
    const res = await apiFetch(`/api/v1/venues?${query}`);
    if (!res.ok) throw new Error(`Venue roster request failed (${res.status})`);
    const page = (await res.json()) as VenueRosterResult;
    rows.push(...page.venues);
    if (page.venues.length === 0 || rows.length >= page.total) return rows;
  }
}

/**
 * The whole page's data.
 *
 * Search and sort are deliberately NOT sent: the roster is tens of rows, it is
 * already in the browser, and filtering it there keeps the map and the count in
 * step with the list without a round trip per keystroke. The endpoint carries
 * both parameters for when a region has enough venues to page.
 */
export function useVenueRoster(request: VenueRosterQuery = {}, enabled = true) {
  const slugs = realSlugs(request.regions);
  return useQuery({
    // The prefix is what note writes invalidate, so keep it first.
    queryKey: [...ROSTER_KEY, slugs.join(',') || 'all', request.hasOpenTasks ? 'tasks' : 'any'],
    queryFn: () => fetchVenueRoster(request),
    enabled,
    staleTime: STALE_30S,
    // A mistyped slug is a stable answer, not a blip worth three more requests.
    retry: (failureCount, error) => !(error instanceof RegionNotFoundError) && failureCount < 2,
  });
}
